import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'antd';
import { CloseOutlined, GiftOutlined, TagOutlined } from '@ant-design/icons';
import Button from 'components/generalUI/Button';
import priceFormat from 'utils/formats/priceFormat';
import { DiscountCardState } from 'views/TicketSummary/ticketSummary.viewModel';
import styles from './DiscountCard.module.scss';

type DiscountCardProps = DiscountCardState;

const DiscountCard: FC<DiscountCardProps> = (props: DiscountCardProps) => {
  const { t } = useTranslation();
  const {
    promotionCodeLabel,
    hasGiftCard,
    giftCardBalance,
    isComplete,
    error,
    loading,
    onApply,
    onRemovePromotionCode,
    onRemoveGiftCard,
  } = props;

  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');

  const onSubmit = () => {
    if (!code.trim()) return;
    onApply(code.trim(), password.trim())
      .then(() => {
        setCode('');
        setPassword('');
      })
      .catch(() => undefined);
  };

  const renderChip = (
    label: string,
    icon: JSX.Element,
    onRemove: () => void,
    variant?: string
  ) => (
    <span className={`${styles.Chip} ${variant || ''}`}>
      {icon}
      <span className={styles.ChipLabel}>{label}</span>
      <button
        className={styles.ChipRemove}
        type="button"
        onClick={onRemove}
        aria-label={t('pages.ticketSummary.discount.remove', { name: label })}
      >
        <CloseOutlined />
      </button>
    </span>
  );

  return (
    <div className={styles.Card}>
      <h3 className={styles.Title}>
        {isComplete
          ? t('pages.ticketSummary.discount.allApplied')
          : t('pages.ticketSummary.discount.title')}
      </h3>

      {(promotionCodeLabel || hasGiftCard) && (
        <div className={styles.Chips}>
          {promotionCodeLabel &&
            renderChip(
              t('pages.ticketSummary.discount.discountChip', {
                code: promotionCodeLabel,
              }),
              <TagOutlined />,
              onRemovePromotionCode,
              styles.ChipDiscount
            )}
          {hasGiftCard &&
            renderChip(
              t('pages.ticketSummary.discount.giftCardChip', {
                amount: priceFormat.format(giftCardBalance),
              }),
              <GiftOutlined />,
              onRemoveGiftCard
            )}
        </div>
      )}

      {!isComplete && (
        <>
          <div className={styles.Fields}>
            <Input
              className={styles.Input}
              value={code}
              placeholder={t('pages.ticketSummary.discount.codePlaceholder')}
              onChange={(event) => setCode(event.target.value)}
              onPressEnter={onSubmit}
            />
            <Button
              kind="secondary"
              className={styles.SubmitButton}
              disabled={!code.trim()}
              loading={loading}
              onClick={onSubmit}
            >
              {t('pages.ticketSummary.discount.apply')}
            </Button>
          </div>

          <Input
            className={styles.Input}
            value={password}
            placeholder={t('pages.ticketSummary.discount.passwordPlaceholder')}
            onChange={(event) => setPassword(event.target.value)}
            onPressEnter={onSubmit}
          />
        </>
      )}

      {error && <p className={styles.Error}>{error}</p>}
    </div>
  );
};

export default DiscountCard;
